/**
 * ECharts truncates labels with ellipses or lets them overflow past the chart bounds when
 * text doesn't fit. In a video the user can't hover to read the full text, so we never want
 * truncation.
 *
 * Walk the config tree and force `overflow: 'none'` on every `label` and `axisLabel` so
 * labels render in full; for `axisLabel` we also disable hide-on-overlap and the auto
 * interval so every category tick is visible.
 *
 * Skips labels explicitly hidden (`show: false`) so we don't accidentally make them visible.
 */
export function applyLabelOverflowDefaults<T>(node: T): T {
    if (Array.isArray(node)) {
        return node.map((item) => applyLabelOverflowDefaults(item)) as unknown as T;
    }
    if (typeof node === 'function' || node === null || node === undefined) return node;
    if (typeof node !== 'object') return node;
    if ('$animate' in (node as Record<string, unknown>)) return node;

    const result: Record<string, unknown> = {};
    for (const key of Object.keys(node as Record<string, unknown>)) {
        const value = applyLabelOverflowDefaults((node as Record<string, unknown>)[key]);
        if (
            (key === 'label' || key === 'axisLabel') &&
            value &&
            typeof value === 'object' &&
            !Array.isArray(value)
        ) {
            const labelObj = value as Record<string, unknown>;
            if (labelObj.show === false) {
                result[key] = labelObj;
            } else if (key === 'axisLabel') {
                result[key] = {
                    ...labelObj,
                    hideOverlap: false,
                    interval: 0,
                    overflow: 'none',
                };
            } else {
                result[key] = { ...labelObj, overflow: 'none' };
            }
        } else {
            result[key] = value;
        }
    }
    return result as T;
}
